import { InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";

import { AppShell } from "@chef/feature-app-shell";
import { getCurrentWeekDataAndBasketFromCalendar } from "@chef/state-management/helpers";
import { NoDelivery } from "@chef/icons/symbol";
import { useFlag } from "@chef/feature-flags";
import { useCalendarData } from "@chef/state-management/hooks";
import { BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import {
  formatDate,
  getNextCutoff,
  getNextCutoffs,
  isMealboxBasketType,
} from "@chef/helpers";
import { Skeleton } from "@chef/components";
import { IPromotion, ISection, IWeekOption } from "@chef/feature-sanity";

import { MyMenuGetStaticProps } from "..";
import { WeeksNavigation } from "../lib/WeeksNavigation";
import { MainDefault } from "./MainDefault";
import { MainExperimental } from "./MainExperimental";

import { intl } from "./MyMenu.Intl";

interface MainComponentProps {
  week: number;
  year: number;
  whyDeliveryChangeSection: ISection | null;
  promotion: IPromotion | null;
  weekOptions: IWeekOption[];
}

const MainComponent = ({
  week,
  year,
  whyDeliveryChangeSection,
  promotion,
  weekOptions,
}: MainComponentProps) => {
  const router = useRouter();

  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const { calendar = [], isLoading } = useCalendarData({
    week,
    year,
  });

  const currentWeek = getCurrentWeekDataAndBasketFromCalendar({
    calendar: calendar,
    week,
    year,
  })?.mealboxBasket;

  const currentWeekInCalendar = calendar.find(
    (weekData) => weekData.week === week && weekData.year === year,
  );

  const currentWeekWithinNext3Cutoffs = getNextCutoffs(3).find(
    (cutoff) => cutoff.week === week && cutoff.year === year,
  );

  const isOutsideRange =
    !currentWeekInCalendar || !currentWeekWithinNext3Cutoffs;

  const isAvailableForDelivery =
    currentWeek?.shippingDetails.plannedDeliveryInformation
      .isAvailableForDelivery;

  const firstWeekWithAvailableForDelivery = calendar.find((weekData) =>
    weekData.baskets.some(
      (basket) =>
        isMealboxBasketType(basket) &&
        basket.shippingDetails.plannedDeliveryInformation
          .isAvailableForDelivery,
    ),
  );

  const nextDeliveryDate = firstWeekWithAvailableForDelivery?.baskets.find(
    (basket) =>
      isMealboxBasketType(basket) &&
      basket.shippingDetails.plannedDeliveryInformation.isAvailableForDelivery,
  )?.shippingDetails.plannedDeliveryInformation.deliveryDate;

  const formattedNextDeliveryDate = nextDeliveryDate
    ? formatDate(new Date(nextDeliveryDate), intl.SHORT_DATE_FORMAT)
    : null;

  if (isLoading) {
    return (
      <AppShell.Main noPadding>
        <div className="flex flex-col gap-4 pb-32 page-padding">
          <Skeleton width="unset" height={40} className="stretch-x" />
          <Skeleton width={80} height={16} />
          <Skeleton width={64} height={8} />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Skeleton height={80} />
            <Skeleton height={80} />
            <Skeleton height={80} />
            <Skeleton height={80} />
            <Skeleton height={80} />
          </div>
        </div>
      </AppShell.Main>
    );
  }

  if (isOutsideRange) {
    const nextCutoff = getNextCutoff();

    if (week !== nextCutoff.week || year !== nextCutoff.year) {
      router.push(
        BRAND_PATHS.MY_MENU_WEEK_HREF({
          week: nextCutoff.week,
          year: nextCutoff.year,
        }),
      );
    }

    return null;
  }

  if (!isAvailableForDelivery) {
    <AppShell.Main>
      <div className="flex flex-col items-center justify-center">
        <div className="flex m-9">
          <NoDelivery className="w-40 h-40 md:w-40 md:h-40" />
        </div>
        <div className="flex flex-col items-center">
          <h2>
            <strong>{intl.TEMPORARY_DELIVERY_STOP}</strong>
          </h2>
          <p className="max-w-screen-sm pt-4 text-center">
            {intl.THIS_WEEK_WE_DO_NOT_DELIVER_TO_YOUR_AREA}
          </p>
          {formattedNextDeliveryDate && (
            <p className="pt-2 text-center text-gray-600">
              {intl.NEXT_DELIVERY_IS} {formattedNextDeliveryDate}
            </p>
          )}
        </div>
      </div>
    </AppShell.Main>;
  }

  if (isWeekEditorRedesignEnabled) {
    return (
      <MainExperimental
        key={week}
        week={week}
        year={year}
        whyDeliveryChangeSection={whyDeliveryChangeSection}
        promotion={promotion}
        weekOptions={weekOptions}
      />
    );
  }

  return (
    <MainDefault
      key={week}
      week={week}
      year={year}
      whyDeliveryChangeSection={whyDeliveryChangeSection}
      promotion={promotion}
      weekOptions={weekOptions}
    />
  );
};

export const MyMenu = (
  props: InferGetStaticPropsType<typeof MyMenuGetStaticProps>,
) => {
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const { week, year, promotion, whyDeliveryChangeSection, weekOptions } =
    props;

  return (
    <AppShell title={intl.MY_MENU}>
      <AppShell.Meta />

      <AppShell.Drawer />

      {isWeekEditorRedesignEnabled ? (
        <>
          <AppShell.Sticky anchor="top" float>
            <AppShell.Header variant="my-menu" />
          </AppShell.Sticky>

          <AppShell.AccountPausedBanner />
          <AppShell.OldVippsBanner />
        </>
      ) : (
        <>
          <AppShell.Header />

          <WeeksNavigation
            week={week}
            year={year}
            sticky
            promotion={promotion}
            weekOptions={weekOptions}
          />

          <AppShell.AccountPausedBanner />
          <AppShell.OldVippsBanner />
        </>
      )}

      <MainComponent
        week={week}
        year={year}
        promotion={promotion}
        whyDeliveryChangeSection={whyDeliveryChangeSection}
        weekOptions={weekOptions}
      />

      <AppShell.Footer variant="minified" />

      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar
          variant={isWeekEditorRedesignEnabled ? "my-menu" : "default"}
        />
      </AppShell.Sticky>
    </AppShell>
  );
};
